import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { AgentSettings, AgentSettingsRequest, GatewayConnection } from '@/api/types'

export function agentSettingsQuery(workspaceId: number) {
  return queryOptions({
    queryKey: qk.agentSettings(workspaceId),
    queryFn: (): Promise<AgentSettings> =>
      unwrap(
        getApi().GET('/api/workspaces/{workspace_id}/settings/big-job-agent/', {
          params: { path: { workspace_id: workspaceId } },
        }),
      ),
  })
}

export function useAgentSettings(workspaceId: number) {
  return useQuery(agentSettingsQuery(workspaceId))
}

/**
 * The credentials the connector gateway is holding for this workspace.
 *
 * A question asked of another service, which is why this is its own query and
 * why it reports its own failure: a gateway that is down must not stop the MCP
 * servers and the standing instructions being edited, and a toast about it
 * would be about neither. The panel says so under the control it affects.
 */
export function agentConnectionsQuery(workspaceId: number, enabled: boolean) {
  return queryOptions({
    queryKey: qk.agentConnections(workspaceId),
    enabled,
    retry: false,
    meta: { skipGlobalErrorHandler: true },
    queryFn: (): Promise<GatewayConnection[]> =>
      unwrap(
        getApi().GET(
          '/api/workspaces/{workspace_id}/settings/big-job-agent/connections/',
          { params: { path: { workspace_id: workspaceId } } },
        ),
      ),
  })
}

export function useAgentConnections(workspaceId: number, enabled: boolean) {
  return useQuery(agentConnectionsQuery(workspaceId, enabled))
}

export function updateAgentMutation(queryClient: QueryClient, workspaceId: number) {
  return mutationOptions({
    // Refused by the backend per row -- a server name that is malformed, taken
    // or reserved -- and the panel shows those on the rows they belong to. See
    // `serverErrorsFrom`.
    meta: { skipGlobalErrorHandler: true },
    mutationFn: (body: AgentSettingsRequest): Promise<AgentSettings> =>
      unwrap(
        getApi().PATCH('/api/workspaces/{workspace_id}/settings/big-job-agent/', {
          params: { path: { workspace_id: workspaceId } },
          // Cast because the request body reuses the response's own component,
          // so it requires the read-only `has_token` -- see
          // `AgentSettingsRequest`.
          body: body as never,
        }),
      ),
    // Written back rather than invalidated: the answer is the whole settings
    // object, and it carries the ids the server assigned to rows that had none
    // -- which the form needs before the next save, or it would create them all
    // over again and delete the ones it had just made.
    onSuccess: (settings) => {
      queryClient.setQueryData(qk.agentSettings(workspaceId), settings)
    },
  })
}

export function useUpdateAgent(workspaceId: number) {
  return useMutation(updateAgentMutation(useQueryClient(), workspaceId))
}
